import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import { ChevronDownIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useTabOptions } from "./useTabOptions";

export interface TabOption {
  id: string;
  value: string;
  icon?: ReactNode;
}

interface DetailsSectionTabsProps {
  selectedId: string | undefined;
  onSelect: (option: TabOption) => void;
  counts?: Record<string, number>;
  isAgentEnabled: boolean;
}

function formatTabLabel(option: TabOption, counts?: Record<string, number>) {
  const count = counts?.[option.id];
  return count ? `${option.value} (${count})` : option.value;
}

export function DetailsSectionTabs({ selectedId, onSelect, counts, isAgentEnabled }: DetailsSectionTabsProps) {
  const tabOptions = useTabOptions(isAgentEnabled);
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(tabOptions.length);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measureContainer = measureRef.current;
    if (!container || !measureContainer) return;

    const calculateVisibleTabs = () => {
      const buttons = Array.from(measureContainer.querySelectorAll<HTMLElement>("button"));
      if (buttons.length === 0) return;

      const containerWidth = container.clientWidth;
      // Last button in the measurement container is the "More" trigger
      const moreButtonWidth = buttons[buttons.length - 1].offsetWidth;
      const tabButtons = buttons.slice(0, -1);
      let usedWidth = 0;

      for (let i = 0; i < tabButtons.length; i++) {
        const buttonWidth = tabButtons[i].offsetWidth;
        const isLast = i === tabButtons.length - 1;
        const availableWidth = isLast ? containerWidth : containerWidth - moreButtonWidth;

        if (usedWidth + buttonWidth > availableWidth) {
          setVisibleCount(Math.max(1, i));
          return;
        }
        usedWidth += buttonWidth;
      }

      setVisibleCount(tabButtons.length);
    };

    calculateVisibleTabs();
    const observer = new ResizeObserver(calculateVisibleTabs);
    observer.observe(container);
    return () => observer.disconnect();
  }, [counts]);

  const visibleTabs = tabOptions.slice(0, visibleCount);
  const overflowTabs = tabOptions.slice(visibleCount);
  const hasOverflow = overflowTabs.length > 0;
  const selectedOverflowTab = overflowTabs.find((tab) => tab.id === selectedId);

  return (
    <div ref={containerRef} className="relative w-full px-4 py-2">
      {/* Hidden container to measure actual tab widths */}
      <div ref={measureRef} className="pointer-events-none invisible absolute" aria-hidden="true">
        <ButtonGroup>
          {tabOptions.map((option) => (
            <Button key={option.id} variant="outline" size="sm" tabIndex={-1}>
              {option.icon}
              {formatTabLabel(option, counts)}
            </Button>
          ))}
          <Button variant="outline" size="sm" className="gap-1" tabIndex={-1}>
            More
            <ChevronDownIcon className="h-4 w-4" />
          </Button>
        </ButtonGroup>
      </div>

      <ButtonGroup aria-label="Details section tabs">
        {visibleTabs.map((option) => (
          <Button
            key={option.id}
            variant="outline"
            size="sm"
            onClick={() => onSelect(option)}
            className={cn(selectedId === option.id && "bg-muted text-foreground")}
          >
            {option.icon}
            {formatTabLabel(option, counts)}
          </Button>
        ))}

        {hasOverflow && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className={cn("gap-1", selectedOverflowTab && "bg-muted text-foreground")}>
                {selectedOverflowTab ? (
                  <>
                    {selectedOverflowTab.icon}
                    {formatTabLabel(selectedOverflowTab, counts)}
                  </>
                ) : (
                  "More"
                )}
                <ChevronDownIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuGroup>
                {overflowTabs.map((option) => (
                  <DropdownMenuItem
                    key={option.id}
                    onClick={() => onSelect(option)}
                    className={cn("cursor-pointer", selectedId === option.id && "bg-muted")}
                  >
                    {option.icon}
                    {formatTabLabel(option, counts)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </ButtonGroup>
    </div>
  );
}
